import { useState, useCallback } from 'react'
import { YStack, XStack, Text, styled } from 'tamagui'
import { Button, GradientText } from '../components/ui'
import { openBookingPage } from '../lib/analytics'
import {
  LOGO_ALT,
  LOGO_MARK_SRC,
  LOGO_SIZE_HERO,
} from '../lib/logo'

const Section = styled(YStack, {
  name: 'CoachingSection',
  width: '100%',
  alignItems: 'center',
  paddingHorizontal: '$6',
  paddingVertical: '$12',
  backgroundColor: '$background',
  '$md': {
    paddingHorizontal: '$4',
    paddingVertical: '$8',
  },
})

const Inner = styled(YStack, {
  name: 'CoachingInner',
  width: '100%',
  maxWidth: 900,
})

const SectionLabel = styled(Text, {
  name: 'CoachingSectionLabel',
  fontFamily: '$accent',
  fontSize: '$3',
  letterSpacing: 3,
  color: '$orangePrimary',
  textTransform: 'uppercase',
})

const StepCard = styled(YStack, {
  name: 'StepCard',
  padding: '$6',
  borderRadius: '$4',
  borderWidth: 1,
  borderColor: '$border',
  backgroundColor: '$cardBackground',
  gap: '$3',
  '$md': {
    padding: '$4',
  },
})

const PriceCard = styled(YStack, {
  name: 'PriceCard',
  padding: '$8',
  borderRadius: '$4',
  borderWidth: 2,
  borderColor: '$orangePrimary',
  backgroundColor: '$cardBackground',
  alignItems: 'center',
  gap: '$4',
  '$md': {
    padding: '$6',
  },
})

const OutputTag = styled(XStack, {
  name: 'OutputTag',
  paddingHorizontal: '$3',
  paddingVertical: '$1',
  borderRadius: '$2',
  borderWidth: 1,
  borderColor: '$border',
  backgroundColor: '$backgroundSecondary',
})

const OnboardStepButton = styled(YStack, {
  name: 'OnboardStepButton',
  padding: '$5',
  borderRadius: '$4',
  borderWidth: 1,
  borderColor: '$border',
  backgroundColor: '$cardBackground',
  cursor: 'pointer',
  gap: '$2',
  hoverStyle: {
    borderColor: '$orangePrimary',
    backgroundColor: '#FF630008',
  },
  variants: {
    active: {
      true: {
        borderColor: '$orangePrimary',
        backgroundColor: '#FF630010',
      },
    },
    completed: {
      true: {
        borderColor: '#333',
        opacity: 0.7,
      },
    },
  } as const,
})

const onboardingSteps = [
  {
    number: '01',
    title: 'Describe what you want to build',
    duration: '~1.5 hrs',
    description: 'A deep intake session where you lay out your vision, your users, your business model, and what success looks like. No jargon required. Just explain what you want in plain language, the way you would to a trusted architect. We ask the follow-ups that surface what you haven\'t thought of yet.',
    preview: [
      'Who is this for? Describe your ideal user.',
      'What problem does this solve that existing tools don\'t?',
      'How will this make money? What are the unit economics?',
      'What does the MVP look like vs the full vision?',
      'What are you most uncertain about?',
    ],
    output: 'Vision Document & User Personas',
  },
  {
    number: '02',
    title: 'Map the rooms and hallways',
    duration: '~2 hrs',
    description: 'Walk through every user journey, step by step, screen by screen. We identify the critical paths, edge cases, error states, and interactions that define your product. This is the floor plan your builder will follow. We don\'t stop at the happy path.',
    preview: [
      'Walk us through the signup flow, every branch.',
      'What happens when a user completes the main action?',
      'How do users come back? What triggers re-engagement?',
      'What are the admin or operator workflows?',
      'What happens when things go wrong? Error states, empty states.',
    ],
    output: 'Workflow Maps & User Journey',
  },
  {
    number: '03',
    title: 'Draw the structural plan',
    duration: '~2 hrs',
    description: 'Your product gets decomposed into clear modules with defined boundaries: the load-bearing walls, the electrical, the plumbing. We recommend the right stack, map the data model in detail, and show how every piece connects. This is the session where architecture decisions get made, not deferred.',
    preview: [
      'Frontend vs backend vs infrastructure layers',
      'Which modules can be built independently?',
      'Data model: every entity, relationship, and constraint',
      'Third-party integrations and API surface',
      'Auth, storage, queues, caching: the full infrastructure picture',
    ],
    output: 'Architecture Diagram & Module Map',
  },
  {
    number: '04',
    title: 'Spec every detail',
    duration: '~2.5 hrs',
    description: 'Each feature gets a thorough specification with inputs, outputs, acceptance criteria, edge cases, and dependencies, written in a format that both humans and AI coding tools can execute from without guesswork. This is the most intensive session. Every ambiguity gets resolved here, not during the build.',
    preview: [
      'Feature: User Authentication (email + OAuth, magic link fallback)',
      'Acceptance: user can sign up, log in, reset password in < 3 steps',
      'Edge case: expired token, rate limiting, account lockout',
      'Dependencies: requires email service, OAuth provider setup',
      'Priority: P0 (launch blocker)',
    ],
    output: 'Requirements Doc & Priority Matrix',
  },
  {
    number: '05',
    title: 'Receive your blueprint',
    duration: '~1 hr review',
    description: 'Everything gets assembled into a phased build plan with scoped SESSION.md files. We walk through the full blueprint together, making sure nothing was missed, priorities are right, and you understand every decision. You leave with artifacts ready to hand to Claude Code, Cursor, an engineering team, or an agency.',
    preview: [
      'Phase 1 (Week 1-2): Auth + data model + API scaffold',
      'Phase 2 (Week 3-4): Core workflows + UI shell',
      'Phase 3 (Week 5-6): Integrations + polish + deploy',
      'Each phase has a SESSION.md with scope, constraints, and "done" criteria',
      'Full walkthrough of how to hand this to your builder',
    ],
    output: 'Build Roadmap & SESSION.md Files',
  },
]

function OnboardingWalkthrough() {
  const [activeStep, setActiveStep] = useState(0)
  const step = onboardingSteps[activeStep]

  return (
    <YStack gap="$6">
      {/* Step selector */}
      <XStack gap="$3" flexWrap="wrap">
        {onboardingSteps.map((s, i) => (
          <YStack
            key={i}
            flexGrow={1}
            flexShrink={0}
            minWidth={140}
            padding="$3"
            borderRadius="$3"
            borderWidth={1}
            borderColor={i === activeStep ? '$orangePrimary' : '$border'}
            backgroundColor={i === activeStep ? '#FF630010' : '$cardBackground'}
            cursor="pointer"
            hoverStyle={{ borderColor: '$orangePrimary' }}
            onPress={() => setActiveStep(i)}
          >
            <Text fontFamily="$mono" color={i === activeStep ? '$orangePrimary' : '$textMuted'} fontSize="$2" fontWeight="700">
              STEP {s.number}
            </Text>
            <Text fontFamily="$body" color={i === activeStep ? '$textPrimary' : '$textSecondary'} fontSize="$3">
              {s.title}
            </Text>
          </YStack>
        ))}
      </XStack>

      {/* Active step detail */}
      <StepCard borderColor="$orangePrimary">
        <YStack gap="$1">
          <XStack gap="$3" alignItems="center" justifyContent="space-between">
            <Text fontFamily="$mono" color="$orangePrimary" fontSize="$4" fontWeight="700">
              STEP {step.number}
            </Text>
            <Text fontFamily="$mono" color="$textMuted" fontSize="$3">
              {step.duration}
            </Text>
          </XStack>
          <Text fontFamily="$heading" fontWeight="600" color="$textPrimary" fontSize="$7">
            {step.title}
          </Text>
        </YStack>

        <Text fontFamily="$body" color="$textSecondary" fontSize="$5" style={{ lineHeight: '1.6em' }}>
          {step.description}
        </Text>

        {/* Preview questions/outputs */}
        <YStack
          gap="$3"
          padding="$5"
          borderRadius="$3"
          backgroundColor="$backgroundSecondary"
          marginTop="$2"
        >
          <Text fontFamily="$mono" color="$textMuted" fontSize="$2" textTransform="uppercase" letterSpacing={2}>
            {activeStep < 3 ? 'Sample questions' : 'Sample output'}
          </Text>
          {step.preview.map((line, i) => (
            <XStack key={i} gap="$3" alignItems="flex-start">
              <Text fontFamily="$mono" color="$orangePrimary" fontSize="$3">
                {activeStep < 3 ? '?' : '>'}
              </Text>
              <Text fontFamily={activeStep < 3 ? '$body' : '$mono'} color="$textSecondary" fontSize="$4" style={{ lineHeight: '1.5em' }}>
                {line}
              </Text>
            </XStack>
          ))}
        </YStack>

        {/* Output badge */}
        <XStack gap="$2" alignItems="center" marginTop="$1">
          <Text fontFamily="$mono" color="$orangePrimary" fontSize="$3">→</Text>
          <Text fontFamily="$mono" color="$orangePrimary" fontSize="$3" fontWeight="600">
            Deliverable: {step.output}
          </Text>
        </XStack>
      </StepCard>

      {/* Navigation */}
      <XStack justifyContent="space-between" alignItems="center">
        <Button
          variant="ghost"
          size="md"
          onPress={() => setActiveStep(Math.max(0, activeStep - 1))}
          opacity={activeStep === 0 ? 0.3 : 1}
          disabled={activeStep === 0}
        >
          ← Previous
        </Button>
        <Text fontFamily="$mono" color="$textMuted" fontSize="$3">
          {activeStep + 1} of {onboardingSteps.length}
        </Text>
        {activeStep < onboardingSteps.length - 1 ? (
          <Button
            variant="ghost"
            size="md"
            onPress={() => setActiveStep(activeStep + 1)}
          >
            Next →
          </Button>
        ) : (
          <Button
            variant="primary"
            size="md"
            onPress={() => openBookingPage('CoachingOnboardComplete')}
          >
            Get Your Blueprint →
          </Button>
        )}
      </XStack>
    </YStack>
  )
}

function scrollToOnboarding() {
  document.getElementById('onboarding')?.scrollIntoView({ behavior: 'smooth' })
}

export function CoachingPage() {
  return (
    <YStack flexGrow={1} flexShrink={0} backgroundColor="$background">
      {/* Hero */}
      <Section style={{ minHeight: '100vh', justifyContent: 'center' }}>
        <Inner alignItems="center" gap="$6">
          <a href="/" style={{ textDecoration: 'none' }}>
            <img
              src={LOGO_MARK_SRC}
              alt={LOGO_ALT}
              width={LOGO_SIZE_HERO}
              height={LOGO_SIZE_HERO}
              style={{ width: LOGO_SIZE_HERO, height: 'auto', cursor: 'pointer' }}
            />
          </a>
          <SectionLabel>LIGHTSTRIKE LABS | THE BLUEPRINT</SectionLabel>

          <Text
            fontFamily="$heading"
            fontWeight="700"
            color="$textPrimary"
            textAlign="center"
            letterSpacing={-2}
            fontSize="$12"
            lineHeight="$12"
            $md={{ fontSize: '$10', lineHeight: '$10' }}
            $sm={{ fontSize: '$8', lineHeight: '$8' }}
          >
            You don't need a builder.{'\n'}
            <GradientText>You need a blueprint.</GradientText>
          </Text>

          <Text
            fontFamily="$body"
            fontSize="$6"
            lineHeight="$7"
            color="$textSecondary"
            textAlign="center"
            maxWidth={700}
            $md={{ fontSize: '$5', lineHeight: '$6' }}
          >
            The knowledge of every software architect, distilled into a self-service system
            that produces build-ready blueprints you can take to any builder, AI or human.
          </Text>

          <YStack width="100%" maxWidth={500} marginTop="$4">
            <Button
              variant="primary"
              size="xl"
              fullWidth
              onPress={scrollToOnboarding}
            >
              See How It Works
            </Button>
          </YStack>

          <Text fontFamily="$body" fontSize="$3" color="$textMuted" textAlign="center">
            $999. One full day. Every decision made. Take your blueprint to any builder.
          </Text>
        </Inner>
      </Section>

      {/* Problem */}
      <Section>
        <Inner gap="$8">
          <SectionLabel>THE GAP</SectionLabel>
          <Text
            fontFamily="$heading"
            fontWeight="700"
            fontSize="$9"
            color="$textPrimary"
            style={{ lineHeight: '1.2em' }}
            $md={{ fontSize: '$7' }}
          >
            You wouldn't build a house without blueprints.{'\n'}
            Why are you building software without them?
          </Text>
          <YStack gap="$6">
            {[
              {
                problem: '"I\'ll build it myself"',
                result: 'AI tools made everyone think they\'re a builder. You watch a few demos, fire up Cursor, and start generating code. It works until the walls start leaning. No foundation, no structure, no plan for what happens when you need a second floor. You\'ve built something you can\'t maintain, extend, or hand off.',
              },
              {
                problem: '"I\'ll go straight to the builder"',
                result: 'You hire an agency or a freelancer and say "build me this." But a builder without blueprints has to become the architect first, on your dime. Half the budget goes to extracting requirements you already know. Endless back-and-forth. Scope creep. The builder keeps asking questions you thought you\'d already answered.',
              },
              {
                problem: '"I\'ll let AI figure it out"',
                result: 'You paste your idea into Claude or ChatGPT and tell it to build. It writes code fast and with total confidence, but without architecture, business logic, or modular boundaries, it produces beautiful slop. A house that looks perfect in the renders but has no plumbing.',
              },
            ].map((item, i) => (
              <XStack key={i} gap="$4" $sm={{ flexDirection: 'column' }}>
                <Text
                  fontFamily="$mono"
                  color="$orangePrimary"
                  fontSize="$3"
                  minWidth={40}
                  style={{ lineHeight: '1.8em' }}
                >
                  0{i + 1}
                </Text>
                <YStack gap="$2" flexGrow={1} flexShrink={1} minWidth={0}>
                  <Text fontFamily="$heading" fontWeight="600" color="$textPrimary" fontSize="$6">
                    {item.problem}
                  </Text>
                  <Text fontFamily="$body" color="$textSecondary" fontSize="$5" style={{ lineHeight: '1.6em' }}>
                    {item.result}
                  </Text>
                </YStack>
              </XStack>
            ))}
          </YStack>
          <Text fontFamily="$body" fontSize="$6" color="$textPrimary" fontWeight="600" marginTop="$4">
            Every one of these fails for the same reason: they skipped the architect.
          </Text>
        </Inner>
      </Section>

      {/* Solution bridge */}
      <Section backgroundColor="$backgroundSecondary">
        <Inner gap="$6" alignItems="center">
          <SectionLabel>THE SHIFT</SectionLabel>
          <Text
            fontFamily="$heading"
            fontWeight="700"
            fontSize="$9"
            color="$textPrimary"
            textAlign="center"
            style={{ lineHeight: '1.2em' }}
            $md={{ fontSize: '$7' }}
          >
            Get the blueprint{'\n'}without the cost of hiring the architect.
          </Text>
          <Text
            fontFamily="$body"
            fontSize="$6"
            color="$textSecondary"
            textAlign="center"
            maxWidth={700}
            style={{ lineHeight: '1.7em' }}
            $md={{ fontSize: '$5' }}
          >
            Getting a real software blueprint used to mean hiring an expensive architect:
            someone with deep relationships, a long track record, and a five-figure retainer. Most people
            couldn't access one, so they either gave up on their idea or jumped straight to a builder and
            paid for it in scope creep and rework.
          </Text>
          <Text
            fontFamily="$body"
            fontSize="$6"
            color="$textPrimary"
            textAlign="center"
            maxWidth={700}
            fontWeight="600"
            style={{ lineHeight: '1.7em' }}
            $md={{ fontSize: '$5' }}
          >
            We built a system with the knowledge of every architect. It asks the right questions,
            identifies the right structures, and produces a blueprint you can take to any builder:
            Claude Code, Cursor, an agency, a CTO, or Lightstrike itself.
          </Text>
          <Text
            fontFamily="$mono"
            fontSize="$5"
            color="$orangePrimary"
            textAlign="center"
            marginTop="$2"
          >
            One blueprint. Any builder. $999.
          </Text>
        </Inner>
      </Section>

      {/* Onboarding Walkthrough */}
      <Section id="onboarding">
        <Inner gap="$8">
          <SectionLabel>YOUR BLUEPRINT</SectionLabel>
          <Text
            fontFamily="$heading"
            fontWeight="700"
            fontSize="$9"
            color="$textPrimary"
            style={{ lineHeight: '1.2em' }}
            $md={{ fontSize: '$7' }}
          >
            Five sessions. One day.{'\n'}A complete blueprint.
          </Text>
          <Text fontFamily="$body" fontSize="$5" color="$textSecondary" style={{ lineHeight: '1.6em' }}>
            A professional blueprint structured to save you future time and money.
            Walk through each session below to see what you'll experience.
          </Text>
          <OnboardingWalkthrough />
        </Inner>
      </Section>

      {/* Take it anywhere */}
      <Section backgroundColor="$backgroundSecondary">
        <Inner gap="$6" alignItems="center">
          <SectionLabel>BUILDER-AGNOSTIC</SectionLabel>
          <Text
            fontFamily="$heading"
            fontWeight="700"
            fontSize="$9"
            color="$textPrimary"
            textAlign="center"
            style={{ lineHeight: '1.2em' }}
            $md={{ fontSize: '$7' }}
          >
            Take your blueprint to any builder
          </Text>
          <XStack gap="$4" flexWrap="wrap" justifyContent="center" marginTop="$2">
            {[
              'Claude Code',
              'Cursor',
              'An agency',
              'Your CTO',
              'A freelancer',
              'Lightstrike Labs',
            ].map((builder, i) => (
              <YStack
                key={i}
                paddingHorizontal="$5"
                paddingVertical="$3"
                borderRadius="$3"
                borderWidth={1}
                borderColor="$border"
                backgroundColor="$cardBackground"
              >
                <Text fontFamily="$mono" color="$textSecondary" fontSize="$4">
                  {builder}
                </Text>
              </YStack>
            ))}
          </XStack>
          <Text fontFamily="$body" fontSize="$5" color="$textSecondary" textAlign="center" maxWidth={600} marginTop="$2" style={{ lineHeight: '1.6em' }}>
            Your blueprint is yours. It's not locked to a platform or vendor.
            Hand it to whoever builds best for you. The artifacts work the same way everywhere.
          </Text>
        </Inner>
      </Section>

      {/* Pricing */}
      <Section>
        <Inner gap="$8">
          <SectionLabel>PRICING</SectionLabel>
          <Text
            fontFamily="$heading"
            fontWeight="700"
            fontSize="$9"
            color="$textPrimary"
            style={{ lineHeight: '1.2em' }}
            $md={{ fontSize: '$7' }}
          >
            The blueprint. Or the blueprint + the architect.
          </Text>

          <XStack gap="$6" $sm={{ flexDirection: 'column' }}>
            {/* Tier 1: The Blueprint */}
            <PriceCard flexGrow={1} flexShrink={1} flexBasis={0} $sm={{ flexBasis: 'auto', flexShrink: 0 }} borderColor="$border" alignItems="flex-start">
              <Text fontFamily="$mono" color="$orangePrimary" fontSize="$3" fontWeight="700" textTransform="uppercase" letterSpacing={2}>
                The Blueprint
              </Text>
              <Text fontFamily="$heading" fontWeight="700" fontSize="$10" color="$textPrimary">
                $999
              </Text>
              <Text fontFamily="$body" color="$textSecondary" fontSize="$4" style={{ lineHeight: '1.6em' }}>
                A full day of structured sessions. Every question answered, every decision documented. Then take your blueprint to any builder you choose.
              </Text>
              <YStack gap="$3" width="100%" marginTop="$2">
                {[
                  'Vision document & user personas',
                  'System architecture & module map',
                  'Requirements with acceptance criteria',
                  'Phased build roadmap',
                  'SESSION.md files for agentic tools',
                  'Tech stack recommendation',
                  'Take it to any builder, AI or human',
                ].map((item, i) => (
                  <XStack key={i} gap="$3" alignItems="flex-start">
                    <Text fontFamily="$mono" color="$orangePrimary" fontSize="$3">+</Text>
                    <Text fontFamily="$body" color="$textSecondary" fontSize="$3" style={{ lineHeight: '1.5em' }}>
                      {item}
                    </Text>
                  </XStack>
                ))}
              </YStack>
              <Text fontFamily="$mono" color="$textMuted" fontSize="$2" style={{ lineHeight: '1.6em' }}>
                Best for: technical founders, teams with engineers, experienced agentic coders
              </Text>
              <Button
                variant="secondary"
                size="lg"
                fullWidth
                marginTop="$3"
                onPress={scrollToOnboarding}
              >
                Get Started
              </Button>
            </PriceCard>

            {/* Tier 2: Blueprint + Architect */}
            <PriceCard flexGrow={1} flexShrink={1} flexBasis={0} $sm={{ flexBasis: 'auto', flexShrink: 0 }} alignItems="flex-start">
              <XStack gap="$3" alignItems="center" flexWrap="wrap">
                <Text fontFamily="$mono" color="$orangePrimary" fontSize="$3" fontWeight="700" textTransform="uppercase" letterSpacing={2}>
                  Blueprint + Architect
                </Text>
                <XStack
                  paddingHorizontal="$2"
                  paddingVertical="$1"
                  borderRadius="$2"
                  backgroundColor="rgba(255, 99, 0, 0.15)"
                >
                  <Text fontFamily="$mono" fontSize="$1" color="$orangePrimary" fontWeight="700">
                    RECOMMENDED
                  </Text>
                </XStack>
              </XStack>
              <Text fontFamily="$heading" fontWeight="700" fontSize="$10" color="$textPrimary">
                $4,999
              </Text>
              <Text fontFamily="$body" color="$textSecondary" fontSize="$4" style={{ lineHeight: '1.6em' }}>
                Everything in The Blueprint, plus a Lightstrike principal engineer reviews your artifacts, refines them, and coaches you through execution.
              </Text>
              <YStack gap="$3" width="100%" marginTop="$2">
                {[
                  'Everything in The Blueprint, enhanced',
                  'Principal engineer artifact review',
                  'Live architecture coaching session',
                  'Custom CI/CD & testing strategy',
                  'Code review of your first milestone',
                  '30-day async support via Slack',
                  '$999 credited toward any Lightstrike project',
                ].map((item, i) => (
                  <XStack key={i} gap="$3" alignItems="flex-start">
                    <Text fontFamily="$mono" color="$orangePrimary" fontSize="$3">+</Text>
                    <Text fontFamily="$body" color={i === 0 ? '$textPrimary' : '$textSecondary'} fontWeight={i === 0 ? '600' : '400'} fontSize="$3" style={{ lineHeight: '1.5em' }}>
                      {item}
                    </Text>
                  </XStack>
                ))}
              </YStack>
              <Text fontFamily="$mono" color="$textMuted" fontSize="$2" style={{ lineHeight: '1.6em' }}>
                Best for: first-time builders, non-technical founders, teams entering agentic development
              </Text>
              <Button
                variant="primary"
                size="lg"
                fullWidth
                marginTop="$3"
                onPress={() => openBookingPage('CoachingEngineering')}
              >
                Get Started
              </Button>
            </PriceCard>
          </XStack>

          {/* Full project upsell */}
          <StepCard borderColor="$border" marginTop="$4">
            <XStack gap="$6" alignItems="center" $sm={{ flexDirection: 'column' }}>
              <YStack flexGrow={1} flexShrink={1} minWidth={0} gap="$2">
                <Text fontFamily="$mono" color="$orangePrimary" fontSize="$3" fontWeight="700" textTransform="uppercase" letterSpacing={2}>
                  NEED THE WHOLE HOUSE BUILT?
                </Text>
                <Text fontFamily="$heading" fontWeight="600" color="$textPrimary" fontSize="$7">
                  Full Build with Lightstrike Labs
                </Text>
                <Text fontFamily="$body" color="$textSecondary" fontSize="$5" style={{ lineHeight: '1.6em' }}>
                  Your blueprint investment applies as credit toward a full Lightstrike engagement.
                  We take your artifacts and build, ship, and scale the product, skipping
                  discovery entirely because the blueprint already exists.
                </Text>
              </YStack>
              <YStack minWidth={200}>
                <Button
                  variant="secondary"
                  size="lg"
                  onPress={() => openBookingPage('CoachingFullProject')}
                >
                  Talk to Us →
                </Button>
              </YStack>
            </XStack>
          </StepCard>
        </Inner>
      </Section>

      {/* CTA */}
      <Section>
        <Inner alignItems="center" gap="$6">
          <Text
            fontFamily="$heading"
            fontWeight="700"
            fontSize="$9"
            color="$textPrimary"
            textAlign="center"
            style={{ lineHeight: '1.2em' }}
            $md={{ fontSize: '$7' }}
          >
            Stop building without a blueprint.
          </Text>
          <Text fontFamily="$body" fontSize="$5" color="$textSecondary" textAlign="center" maxWidth={600} style={{ lineHeight: '1.6em' }}>
            Whether you build it yourself, hand it to an AI, or hire a team, the blueprint
            is what makes the difference between a product that ships and a project that stalls.
          </Text>
          <XStack gap="$4" $sm={{ flexDirection: 'column', width: '100%' }}>
            <Button
              variant="primary"
              size="xl"
              onPress={scrollToOnboarding}
            >
              Get Your Blueprint
            </Button>
            <Button
              variant="secondary"
              size="xl"
              onPress={() => openBookingPage('CoachingBottomCta')}
            >
              Blueprint + Architect
            </Button>
          </XStack>
        </Inner>
      </Section>

      {/* Footer */}
      <YStack
        paddingHorizontal="$6"
        paddingVertical="$6"
        alignItems="center"
        borderTopWidth={1}
        borderTopColor="$border"
      >
        <XStack gap="$4" alignItems="center">
          <Text fontFamily="$body" fontSize="$3" color="$textMuted">
            © 2026 Lightstrike LLC
          </Text>
          <Text fontFamily="$body" fontSize="$3" color="$textMuted">·</Text>
          <a href="/" style={{ textDecoration: 'none' }}>
            <Text fontFamily="$body" fontSize="$3" color="$textMuted" cursor="pointer">
              Home
            </Text>
          </a>
          <Text fontFamily="$body" fontSize="$3" color="$textMuted">·</Text>
          <a href="/privacy-policy" style={{ textDecoration: 'none' }}>
            <Text fontFamily="$body" fontSize="$3" color="$textMuted" cursor="pointer">
              Privacy
            </Text>
          </a>
          <Text fontFamily="$body" fontSize="$3" color="$textMuted">·</Text>
          <a href="/terms-of-service" style={{ textDecoration: 'none' }}>
            <Text fontFamily="$body" fontSize="$3" color="$textMuted" cursor="pointer">
              Terms
            </Text>
          </a>
        </XStack>
      </YStack>
    </YStack>
  )
}
